<template>
    <div class="list-price-grid">
        <div class="list-price-grid__header">
            {{ toSentenceCase($tkey('listPrice')) }}
        </div>
        <promo-ag-grid
            :row-data="rowData"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :selection-column-def="selectionColumnDef"
            :grid-options="gridOptions"
            grid-style="width: 100%; height: 35rem;"
            grid-class="ag-theme-custom__candidates-grid"
            dom-layout="normal"
            :single-click-edit="true"
            :make-read-only="promotionEditingState.disabled"
            :read-only-reason="promotionEditingState.reason"
            :class="{
                'selection-disabled': isReadOnly,
            }"
            @cell-value-changed="onCellValueChanged"
            @grid-ready="onGridReady"
            @first-data-rendered="firstDataRendered"
        />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { isEmpty, get } from 'lodash';
import promotionTabs from '@enums/promotion-tabs';
import { toSentenceCase } from '@/js/utils/string-utils';
import AgCurrencyInput from '@/js/components/promo-ag-grid/ag-currency-input.vue';

export default {
    localizationKey: 'planning.promotionsMaintenance.resources.featuredProducts.gridHeadings',

    props: {
        hiddenColumns: {
            type: Array,
            default: () => [],
        },
        namespace: {
            required: true,
            type: String,
        },
        promotionEditingState: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            gridApi: null,
            rowData: [],
            // Defines the attributes which apply to all columns defined in columnDefs.
            defaultColDef: {
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                sortable: true, // All columns default to being sortable.
                unSortIcon: true, // Ensures the sort icon displays all the time (not just when hovered over).
            },
            gridOptions: markRaw({
                rowHeight: 35, // Specified in pixels.
                headerHeight: 120,
                getRowId: ({ data }) => String(data.productKey),
                rowSelection: {
                    mode: 'multiRow',
                    checkboxes: () => {
                        return !this.isReadOnly;
                    },
                },
                onRowSelected: this.onRowSelected,
                onSelectionChanged: this.onSelectionChanged,
                context: {
                    isReadOnly: () => this.isReadOnly,
                },
            }),
            selectionColumnDef: {
                maxWidth: 0,
            },
        };
    },

    events: {
        onCandidateRemovedFromPromotion() {
            this.gridApi.setGridOption('rowData', this.dataForGrid);
        },
        onCandidateAddedToPromotion() {
            this.gridApi.setGridOption('rowData', this.dataForGrid);
        },
    },

    computed: {
        ...mapGetters('promotions', ['getStagingAreaPromotionById']),

        dataForGrid() {
            return this.selectedPromotion.products;
        },

        columnDefs() {
            const columns = [
                {
                    headerName: toSentenceCase(this.$tkey('productKey')),
                    field: 'clientProductKey',
                    cellClass: ['product-information'],
                    maxWidth: 80,
                    minWidth: 80,
                    pinned: 'left',
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('productName')),
                    field: 'name',
                    cellClass: ['product-information', 'product-information__name'],
                    maxWidth: 200,
                    minWidth: 170,
                    pinned: 'left',
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('brandName')),
                    field: 'brandDescription',
                    cellClass: ['product-information'],
                    maxWidth: 90,
                    minWidth: 90,
                    filter: true,
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('supplierName')),
                    field: 'supplierName',
                    cellClass: ['product-information'],
                    maxWidth: 120,
                    minWidth: 120,
                    filter: true,
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('sizeAndUnits')),
                    valueGetter: this.getFormattedProductSizeAndUnit,
                    cellClass: ['product-information', 'product-information--last-column'],
                    maxWidth: 80,
                    minWidth: 80,
                    filter: true,
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('regularPrice')),
                    field: 'nonPromoPrices.avgPrice',
                    valueFormatter: params =>
                        this.formatCellValueAsCurrency(params, 'currencyPadded'),
                    cellClass: ['product-information__right-border'],
                    maxWidth: 100,
                    minWidth: 80,
                    filter: 'agNumberColumnFilter',
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('listPriceSameAsRegular')),
                    colId: 'ag-Grid-SelectionColumn',
                    maxWidth: 80,
                    minWidth: 50,
                    sortable: false,
                    unSortIcon: false,
                    suppressHeaderMenuButton: true,
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('manualListPrice')),
                    field: 'nonPromoListPrice',
                    cellRenderer: AgCurrencyInput,
                    cellRendererParams: {
                        onInputChange: params => {
                            this.onCellValueChanged(params);
                        },
                        isDisabled: data => {
                            return get(data, 'nonPromoListPriceSameAsSystem', false);
                        },
                    },
                    maxWidth: 100,
                    minWidth: 90,
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('incrementalSales')),
                    field: 'forecastingAggregations.product.incrementalSalesExcTax',
                    valueFormatter: this.formatCellValueAsCurrency,
                    cellClass: ['metrics', 'metrics__incremental'],
                    maxWidth: 95,
                    minWidth: 95,
                    filter: 'agNumberColumnFilter',
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('salesEfficiency')),
                    field: 'forecastingAggregations.product.salesEfficiency',
                    valueFormatter: this.formatCellValueAsDecimal,
                    cellClass: ['metrics', 'metrics__margin'],
                    maxWidth: 95,
                    minWidth: 95,
                    filter: 'agNumberColumnFilter',
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('incrementalMargin')),
                    field: 'forecastingAggregations.product.incrementalMargin',
                    valueFormatter: this.formatCellValueAsCurrency,
                    cellClass: ['metrics', 'metrics__incremental'],
                    maxWidth: 95,
                    minWidth: 95,
                    filter: 'agNumberColumnFilter',
                    resizable: false,
                },
                {
                    headerName: toSentenceCase(this.$tkey('marginEfficiency')),
                    field: 'forecastingAggregations.product.marginEfficiency',
                    valueFormatter: this.formatCellValueAsDecimal,
                    cellClass: ['metrics', 'metrics__margin'],
                    maxWidth: 95,
                    minWidth: 95,
                    filter: 'agNumberColumnFilter',
                    resizable: false,
                },
            ];

            if (isEmpty(this.hiddenColumns)) {
                return columns;
            }

            return columns.filter(column => !this.hiddenColumns.includes(column.field));
        },

        selectedPromotion() {
            return this.getStagingAreaPromotionById(this.namespace);
        },
    },

    methods: {
        ...mapActions('promotions', ['setPromotionProductListPrice', 'setStagingAreaFields']),
        ...mapMutations('promotions', ['setUnsavedPromotion']),

        onGridReady(params) {
            this.gridApi = markRaw(params.api);
            this.gridApi.setGridOption('rowData', this.dataForGrid);
        },

        firstDataRendered(params) {
            this.gridApi = markRaw(params.api);

            const nodesToSelect = [];
            this.gridApi.forEachNode(node => {
                if (node.data?.nonPromoListPriceSameAsSystem) {
                    nodesToSelect.push(node);
                }
            });
            this.gridApi.setNodesSelected({
                nodes: nodesToSelect,
                newValue: true,
                source: 'initialSelection',
            });
        },

        formatCellValueAsCurrency(params, format) {
            const currencyFormat = format || 'currencyShorthand';
            return this.$n(`numbers.default.${currencyFormat}`, params.value, {
                usePlaceholder: true,
            });
        },

        formatCellValueAsDecimal(params) {
            return this.$n('numbers.default.numberWithDecimals', params.value, {
                usePlaceholder: true,
            });
        },

        getFormattedProductSizeAndUnit(params) {
            if (params.data.packSize !== undefined && params.data.packUnit) {
                return `${params.data.packSize}${params.data.packUnit}`;
            }
        },

        onCellValueChanged({ data, newValue, oldValue, colDef }) {
            if (newValue !== oldValue) {
                if (colDef.field === 'nonPromoListPriceSameAsSystem') {
                    this.setPromotionProductListPrice({
                        productKey: data.productKey,
                        // Reset nonPromoListPrice when nonPromoListPriceSameAsSystem
                        // has been selected.
                        nonPromoListPrice: newValue ? null : data.nonPromoListPrice,
                        nonPromoListPriceSameAsSystem: newValue,
                        namespace: this.namespace,
                    });
                } else if (colDef.field === 'nonPromoListPrice') {
                    // Keep node.data in sync so that a subsequent checkbox change sees
                    // the latest typed value and produces a real params.value change,
                    // which is required for AgCurrencyInput.created() to re-initialise
                    // with the correct cleared value.
                    data.nonPromoListPrice = newValue;
                    this.setPromotionProductListPrice({
                        productKey: data.productKey,
                        nonPromoListPrice: newValue,
                        nonPromoListPriceSameAsSystem: data.nonPromoListPriceSameAsSystem,
                        namespace: this.namespace,
                    });
                }
            }
        },

        onRowSelected(event) {
            const { source, node, data } = event;
            if (source !== 'checkboxSelected') return;

            const isSelected = node.isSelected();
            const listPrice = isSelected ? null : data.nonPromoListPrice;

            // Null out node.data before applyTransaction + refreshCells so the
            // recreated AgCurrencyInput.created() reads the correct cleared value.
            if (isSelected) {
                data.nonPromoListPrice = null;
            }

            this.gridApi.applyTransaction({
                update: [{ ...data, nonPromoListPriceSameAsSystem: isSelected }],
            });
            this.gridApi.refreshCells({
                rowNodes: [node],
                columns: ['nonPromoListPrice'],
                force: true,
            });

            this.setPromotionProductListPrice({
                productKey: data.productKey,
                nonPromoListPrice: listPrice,
                nonPromoListPriceSameAsSystem: isSelected,
                namespace: this.namespace,
            });
        },

        onSelectionChanged(event) {
            if (!event.source?.includes('SelectAll')) return;

            const rowCount = event.selectedNodes?.length;
            this.selectUnselectAll(!!rowCount);
        },

        selectUnselectAll(isSelected) {
            const updates = [];
            const rowNodes = [];
            this.gridApi.forEachNodeAfterFilter(rowNode => {
                const data = rowNode.data;
                if (isSelected !== !!data.nonPromoListPriceSameAsSystem) {
                    data.nonPromoListPriceSameAsSystem = isSelected;
                    updates.push(data);
                    rowNodes.push(rowNode);
                }
            });

            this.onSelectUnSelectAll({ updates, isSelected });

            this.gridApi.applyTransaction({ update: updates });
            this.gridApi.refreshCells({
                rowNodes,
                columns: ['nonPromoListPrice'],
                force: true,
            });
        },

        onSelectUnSelectAll({ updates, isSelected }) {
            const productUpdates = [];
            updates.forEach((product, ix) => {
                if (isSelected) {
                    // Reset nonPromoListPrice when nonPromoListPriceSameAsSystem
                    // has been selected.
                    product.nonPromoListPrice = null;
                }

                productUpdates.push({
                    fieldPath: `products[${ix}]`,
                    fieldName: 'nonPromoListPrice',
                    value: product.nonPromoListPrice,
                });

                productUpdates.push({
                    fieldPath: `products[${ix}]`,
                    fieldName: 'nonPromoListPriceSameAsSystem',
                    value: product.nonPromoListPriceSameAsSystem,
                });
            });

            this.setStagingAreaFields({
                namespace: this.namespace,
                fieldsToUpdate: productUpdates,
            });
            this.setUnsavedPromotion({
                namespace: this.namespace,
                tab: promotionTabs.channels,
                value: true,
            });
        },
    },
};
</script>

<style scoped lang="scss">
.list-price-grid {
    border-bottom: solid;
    border-bottom-width: 0.75rem !important;
    border-image: linear-gradient(to top, rgba(204, 204, 204, 0.7), rgba(203, 203, 203, 0)) 0 1 100%;

    &__header {
        margin-top: 1.5rem;
    }

    :deep() {
        .ag-root-wrapper {
            min-height: 35rem;
        }

        .ag-checkbox-input-wrapper {
            font-size: 1.5rem;
        }

        .ag-header-cell[col-id='ag-Grid-SelectionColumn'] {
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            padding-bottom: 2rem;

            .ag-header-cell-label {
                order: 1;
                width: 100%;
            }

            .ag-header-select-all {
                order: 2;
            }

            .ag-header-cell-comp-wrapper {
                margin-top: 2rem;
            }
        }
    }

    :deep(.selection-disabled) {
        .ag-header-select-all {
            opacity: 0.5;
            pointer-events: none;
            cursor: not-allowed;
        }
    }
}
</style>
